import React, { useEffect, useState } from 'react';
import { Button, Chip, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import {
    SegmentBalanceSheetResponse,
    SegmentBalanceSheetBlock,
    SegmentReportLine,
    Segment,
} from '../../interfaces/accounting.interface';
import { AccountService } from '../../services/account.service';
import { SegmentService } from '../../services/segment.service';
import { useToast } from '../../context/ToastContext';

const tableHeaderStyle = {
    padding: '12px',
    textAlign: 'left' as const,
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #ddd',
    fontWeight: 600,
};

const tableCellStyle = {
    padding: '10px 12px',
    borderBottom: '1px solid #eee',
};

const formatAmount = (value: number) =>
    value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const todayStr = new Date().toISOString().split('T')[0];

const SegmentBalanceSheet: React.FC = () => {
    const toast = useToast();
    const [report, setReport] = useState<SegmentBalanceSheetResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [asOf, setAsOf] = useState(todayStr);
    const [segments, setSegments] = useState<Segment[]>([]);
    const [selectedSegmentId, setSelectedSegmentId] = useState<string>('');

    useEffect(() => {
        const loadSegments = async () => {
            try {
                const page = await SegmentService.getByInstitution();
                setSegments(page.content ?? []);
            } catch (err) {
                toast.showApiError(err, 'Failed to load segments');
            }
        };
        loadSegments();
    }, []);

    const fetchReport = async () => {
        try {
            setLoading(true);
            const segmentId = selectedSegmentId || undefined;
            const data = await AccountService.getBalanceSheet(asOf, segmentId);
            setReport(data);
        } catch (err) {
            toast.showApiError(err, 'Failed to load segment balance sheet');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const hasData = report && report.segments.length > 0;

    const renderLineTable = (lines: SegmentReportLine[]) => (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '4px' }}>
            <thead>
                <tr>
                    <th style={tableHeaderStyle}>Code</th>
                    <th style={tableHeaderStyle}>Account Name</th>
                    <th style={{ ...tableHeaderStyle, textAlign: 'right' as const }}>Amount</th>
                </tr>
            </thead>
            <tbody>
                {lines.map((line, i) => (
                    <tr key={line.accountId} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                        <td style={tableCellStyle}>{line.accountCode}</td>
                        <td style={tableCellStyle}>{line.accountName}</td>
                        <td style={{ ...tableCellStyle, textAlign: 'right' }}>{formatAmount(line.netBalance)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    return (
        <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ margin: 0 }}>Segment Balance Sheet</h2>
            </div>

            {/* Controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
                <label style={{ fontWeight: 500 }}>
                    As Of:&nbsp;
                    <input
                        type="date"
                        value={asOf}
                        onChange={e => setAsOf(e.target.value)}
                        style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                </label>
                <FormControl size="small" style={{ minWidth: 200 }}>
                    <InputLabel id="segment-filter-label">Segment</InputLabel>
                    <Select
                        labelId="segment-filter-label"
                        label="Segment"
                        value={selectedSegmentId}
                        onChange={e => setSelectedSegmentId(e.target.value as string)}
                    >
                        <MenuItem value="">All Segments</MenuItem>
                        {segments.map(seg => (
                            <MenuItem key={seg.id} value={seg.id ?? ''}>
                                {seg.code} — {seg.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button variant="contained" onClick={fetchReport} disabled={loading}>
                    {loading ? 'Loading...' : 'Apply'}
                </Button>
            </div>

            {/* Loading state */}
            {loading && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>Loading...</div>
            )}

            {/* Empty state */}
            {!loading && !hasData && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                    No data available for the selected date.
                </div>
            )}

            {/* Segment blocks */}
            {!loading && hasData && report!.segments.map((block: SegmentBalanceSheetBlock) => (
                <div key={block.segmentId ?? 'unassigned'} style={{ marginBottom: '32px' }}>
                    {/* Segment header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                        <span style={{ fontWeight: 700, fontSize: '1rem' }}>
                            {block.segmentCode ? `${block.segmentCode} — ` : ''}{block.segmentName}
                        </span>
                        <Chip
                            label={block.balanced ? 'Balanced' : 'Unbalanced'}
                            size="small"
                            color={block.balanced ? 'success' : 'error'}
                        />
                    </div>

                    {/* Assets section */}
                    <div style={{ marginBottom: '8px' }}>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#555', marginBottom: '4px', paddingLeft: '4px' }}>
                            Assets
                        </div>
                        {renderLineTable(block.assetLines)}
                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '12px' }}>
                            <tbody>
                                <tr style={{ backgroundColor: '#e3f2fd', fontWeight: 700, borderTop: '2px solid #90caf9' }}>
                                    <td style={tableCellStyle} colSpan={2}>Total Assets</td>
                                    <td style={{ ...tableCellStyle, textAlign: 'right', color: '#1565c0' }}>
                                        {formatAmount(block.totalAssets)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* Liabilities section */}
                    <div style={{ marginBottom: '8px' }}>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#555', marginBottom: '4px', paddingLeft: '4px' }}>
                            Liabilities
                        </div>
                        {renderLineTable(block.liabilityLines)}
                    </div>

                    {/* Equity section */}
                    <div style={{ marginBottom: '8px' }}>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#555', marginBottom: '4px', paddingLeft: '4px' }}>
                            Equity
                        </div>
                        {renderLineTable(block.equityLines)}
                        {/* Current Period Earnings row */}
                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '4px' }}>
                            <tbody>
                                <tr style={{ backgroundColor: '#fafafa' }}>
                                    <td style={tableCellStyle} colSpan={2} />
                                    <td style={tableCellStyle} />
                                </tr>
                                <tr style={{ backgroundColor: '#fff8e1' }}>
                                    <td style={tableCellStyle}>—</td>
                                    <td style={tableCellStyle}>Current Period Earnings</td>
                                    <td style={{ ...tableCellStyle, textAlign: 'right', fontStyle: 'italic' }}>
                                        {formatAmount(block.currentPeriodEarnings)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        {/* Total Liabilities & Equity row */}
                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '12px' }}>
                            <tbody>
                                <tr style={{ backgroundColor: '#f3e5f5', fontWeight: 700, borderTop: '2px solid #ce93d8' }}>
                                    <td style={tableCellStyle} colSpan={2}>Total Liabilities &amp; Equity</td>
                                    <td style={{ ...tableCellStyle, textAlign: 'right', color: '#6a1b9a' }}>
                                        {formatAmount(block.totalLiabilitiesAndEquity)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            ))}

            {/* Grand-total footer */}
            {!loading && hasData && (
                <div style={{ marginTop: '8px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <tbody>
                            <tr style={{ backgroundColor: '#e3f2fd', fontWeight: 700, borderTop: '3px solid #42a5f5' }}>
                                <td style={tableCellStyle} colSpan={2}>Total Assets</td>
                                <td style={{ ...tableCellStyle, textAlign: 'right', color: '#1565c0' }}>
                                    {formatAmount(report!.totalAssets)}
                                </td>
                            </tr>
                            <tr style={{ backgroundColor: '#f3e5f5', fontWeight: 700 }}>
                                <td style={tableCellStyle} colSpan={2}>Total Liabilities &amp; Equity</td>
                                <td style={{ ...tableCellStyle, textAlign: 'right', color: '#6a1b9a' }}>
                                    {formatAmount(report!.totalLiabilitiesAndEquity)}
                                </td>
                            </tr>
                            <tr style={{ backgroundColor: '#e8eaf6', fontWeight: 700, borderTop: '2px solid #7986cb' }}>
                                <td style={tableCellStyle} colSpan={2}>
                                    <span style={{ marginRight: '12px' }}>Balance Check</span>
                                    <Chip
                                        label={report!.balanced ? 'Balanced' : 'Unbalanced'}
                                        size="small"
                                        color={report!.balanced ? 'success' : 'error'}
                                    />
                                </td>
                                <td style={tableCellStyle} />
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default SegmentBalanceSheet;
